import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, PencilIcon } from 'lucide-react';
import { useFormState, FormData } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { TCPAConsent } from '../components/TCPAConsent';
import { usePageMeta } from '../hooks/usePageMeta';
import { educationLevels, degreeInterestOptions, focusOptions, specializationOptions } from '../data/degrees';
interface LocationState {
  degreeInterest: string;
  areaOfStudy: string;
  specialization: string;
}
export function ExpandedFormPage() {
  usePageMeta('Complete Your Profile | findonlinecollege.com', 'Fill out your profile to get matched with accredited online degree programs that fit your goals, schedule, and budget.', {
    ogImage: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&h=630&fit=crop&q=80',
    ogType: 'website',
    noindex: true
  });
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState | null;
  // Redirect if no state
  useEffect(() => {
    if (!state) {
      navigate('/', {
        replace: true
      });
    }
    window.scrollTo(0, 0);
  }, [state, navigate]);
  const {
    formData,
    errors,
    updateFormData,
    validateExpandedForm
  } = useFormState(state ? {
    degreeInterest: state.degreeInterest,
    areaOfStudy: state.areaOfStudy,
    specialization: state.specialization
  } : undefined);
  if (!state) return null;
  // Resolve labels for the summary bar
  const degreeName = degreeInterestOptions.find(d => d.value === state.degreeInterest)?.type || '';
  const focusName = focusOptions.find(f => f.value === state.areaOfStudy)?.type || '';
  const specName = specializationOptions.find(s => s.value === state.specialization)?.type || '';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateExpandedForm()) {
      // TODO: Send formData to matching API here
      const cleanedFormData = {
        ...formData,
        phone: stripPhone(formData.phone)
      };
      console.log('Form submitted:', cleanedFormData);
      navigate('/results', {
        state: {
          formData: cleanedFormData
        }
      });
    }
  };
  const selectClasses = (field: keyof FormData) => `block w-full px-3 py-3 text-sm border rounded-lg bg-white appearance-none cursor-pointer ${errors[field] ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const inputClasses = (field: keyof FormData) => `block w-full px-3 py-3 text-sm border rounded-lg shadow-sm ${errors[field] ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`;
  const radioGroupClasses = (field: keyof FormData, value: string) => `flex-1 cursor-pointer border rounded-lg py-3 px-4 text-center text-sm font-medium transition-all duration-150 ${formData[field] === value ? 'border-navy bg-navy/5 text-navy ring-1 ring-navy' : 'border-gray-300 text-gray-600 hover:border-navy/40'}`;
  return <main className="flex-grow bg-light min-h-screen" data-id="element-1363" data-mp="2yceu0dpqvb7k">
      {/* Top bar with selections summary */}
      <div className="hero-bg text-white" data-id="element-1364" data-mp="33aq6kw7fbzth">
        <div className="max-w-2xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-1365" data-mp="339ovwsublfca">
          <div className="flex items-center gap-2 text-sm" data-id="element-1366" data-mp="2og3hpnq5mld4">
            <ShieldCheckIcon className="w-4 h-4 text-gold flex-shrink-0" data-id="element-1367" data-mp="2te4z847xmcmh" />
            <span className="text-gray-300" data-id="element-1368" data-mp="2fdu8r2ajb01u">Your selections:</span>
            <span className="font-semibold" data-id="element-1369" data-mp="2ffdmom8pwz0g">{degreeName}</span>
            <span className="text-gray-400" data-id="element-1370" data-mp="2kd6tnhhuhx1s">›</span>
            <span className="font-semibold" data-id="element-1371" data-mp="dlxxltsa3s7l">{focusName}</span>
            <span className="text-gray-400" data-id="element-1372" data-mp="dmh8iqjihgc4">›</span>
            <span className="font-semibold text-gold" data-id="element-1373" data-mp="ilkbm5fxlchg">{specName}</span>
          </div>
          <Link to="/" className="flex items-center gap-1 text-xs text-gray-300 hover:text-white transition-colors" data-id="element-1374" data-mp="2eo5ma7p4naf1">
            <PencilIcon className="w-3 h-3" data-id="element-1375" data-mp="29okmjv24uy19" />
            Edit
          </Link>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="max-w-2xl mx-auto px-4 py-8 sm:py-12" data-id="element-1376" data-mp="24p7yx83ftmxm">
        {/* Progress */}
        <div className="mb-8" data-id="element-1377" data-mp="1vre4k2p8h3d3">
          <div className="flex justify-between items-center mb-2" data-id="element-1378" data-mp="1qyun2unfdo30">
            <span className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-1379" data-mp="1qwi208mdexlq">
              Step 2 of 2
            </span>
            <span className="text-xs font-medium text-gray-500" data-id="element-1380" data-mp="1h0x2qi8osbfo">
              Almost done!
            </span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden" data-id="element-1381" data-mp="1cxs4irvn2vwu">
            <div className="h-full bg-gold rounded-full w-1/2 transition-all duration-500" data-id="element-1382" data-mp="17zgrk8a1s5ae" />
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden" data-id="element-1383" data-mp="3jp6hdilhndjw">
          <div className="bg-navy px-6 py-4" data-id="element-1384" data-mp="3okc42jmpsw91">
            <h1 className="text-white font-bold text-lg tracking-wide text-center" data-id="element-1385" data-mp="3sn8rcnn79gjb">
              Complete Your Profile
            </h1>
            <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1386" data-mp="1gxiw6fv1dhy">
              Tell us a bit about yourself so we can find the best matches.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6" data-id="element-1387" data-mp="34xeazw3o99ub">
            {/* Education Level */}
            <div data-id="element-1388" data-mp="39wh7gs5l67qo">
              <label htmlFor="currentEducation" className="block text-sm font-semibold text-gray-800 mb-1.5" data-id="element-1389" data-mp="39vy0u7ldz5ry">
                Highest Level of Education Completed
              </label>
              <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses('currentEducation')} data-id="element-1390" data-mp="2h7ttzir2wmno">
                <option value="" data-id="element-1391" data-mp="3phatxq2veefh">-- Please Select --</option>
                {educationLevels.map(level => <option key={level} value={level} data-id="element-1392" data-mp="3kig1rxj0jdip">
                    {level}
                  </option>)}
              </select>
              {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-1393" data-mp="3u9amuykiw72k">
                  {errors.currentEducation}
                </p>}
            </div>

            {/* US Citizen */}
            <div data-id="element-1394" data-mp="36jzw89wvzt25">
              <label className="block text-sm font-semibold text-gray-800 mb-2" data-id="element-1395" data-mp="36lkplw7gt5yf">
                Are you a U.S. Citizen?
              </label>
              <div className="flex gap-3" data-id="element-1396" data-mp="3fko7wxyt5hbv">
                <div onClick={() => updateFormData('isUSCitizen', 'yes')} className={radioGroupClasses('isUSCitizen', 'yes')} role="radio" aria-checked={formData.isUSCitizen === 'yes'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isUSCitizen', 'yes')} data-id="element-1397" data-mp="3fibhdwezhyn7">
                  Yes
                </div>
                <div onClick={() => updateFormData('isUSCitizen', 'no')} className={radioGroupClasses('isUSCitizen', 'no')} role="radio" aria-checked={formData.isUSCitizen === 'no'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isUSCitizen', 'no')} data-id="element-1398" data-mp="yc4ggvmbf78p">
                  No
                </div>
              </div>
              {errors.isUSCitizen && <p className="mt-1 text-xs text-red-600" data-id="element-1399" data-mp="tdt7en3uh0rj">
                  {errors.isUSCitizen}
                </p>}
            </div>

            {/* Military */}
            <div data-id="element-1400" data-mp="1m27511r0tv2k">
              <label className="block text-sm font-semibold text-gray-800 mb-2" data-id="element-1401" data-mp="1m0nnz8xjjfzq">
                U.S. Military Affiliation?
              </label>
              <div className="flex gap-3" data-id="element-1402" data-mp="17aoq779we6ks">
                <div onClick={() => updateFormData('isMilitary', 'yes')} className={radioGroupClasses('isMilitary', 'yes')} role="radio" aria-checked={formData.isMilitary === 'yes'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isMilitary', 'yes')} data-id="element-1403" data-mp="1cb45w5hnjq5x">
                  Yes
                </div>
                <div onClick={() => updateFormData('isMilitary', 'no')} className={radioGroupClasses('isMilitary', 'no')} role="radio" aria-checked={formData.isMilitary === 'no'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && updateFormData('isMilitary', 'no')} data-id="element-1404" data-mp="4qz0mkzpomc5">
                  No
                </div>
              </div>
              {errors.isMilitary && <p className="mt-1 text-xs text-red-600" data-id="element-1405" data-mp="3n23r74uiagum">{errors.isMilitary}</p>}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 pt-6" data-id="element-1406" data-mp="ip6xkswqznnb">
              <h3 className="text-sm font-semibold text-gray-800 mb-4" data-id="element-1407" data-mp="inlyqr3ehijn">
                Contact Information
              </h3>
            </div>

            {/* Name Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1408" data-mp="9oig1o85b55o">
              <div data-id="element-1409" data-mp="2zem3pli9y4xg">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1410" data-mp="2zf3vo5xwlwzk">
                  First Name
                </label>
                <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses('firstName')} placeholder="Jane" data-id="element-1411" data-mp="19x6yw2so4ooj" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-1412" data-mp="1ixdatk8pxgll">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-1413" data-mp="1nq6es5wd4kwm">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1414" data-mp="1nqpvjjaa1392">
                  Last Name
                </label>
                <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses('lastName')} placeholder="Doe" data-id="element-1415" data-mp="1xlqaosaxfjld" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-1416" data-mp="26m6clxc0uio4">{errors.lastName}</p>}
              </div>
            </div>

            {/* Email */}
            <div data-id="element-1417" data-mp="8a7o68ish7k4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1418" data-mp="c96n9chuaa3n">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses('email')} placeholder="jane.doe@example.com" data-id="element-1419" data-mp="1fj1rygenqbat" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-1420" data-mp="1ogu9lr7upv1d">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div data-id="element-1421" data-mp="1tevri8tjjob9">
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1422" data-mp="1ygkzfflgvlv4">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses('phone')} placeholder="(555)123-4567" maxLength={13} data-id="element-1423" data-mp="22gdfseh3qwb1" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-1424" data-mp="2c9kfpjt2pk3q">{errors.phone}</p>}
            </div>

            {/* Address + Zip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1425" data-mp="2m5e0trkuylx4">
              <div className="sm:col-span-2" data-id="element-1426" data-mp="2m3jf4b1riprz">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1427" data-mp="ajlmcgpr70wy">
                  Street Address{' '}
                  <span className="text-gray-400 font-normal" data-id="element-1428" data-mp="5lk4fz42d7n2">(optional)</span>
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => updateFormData('address', e.target.value)} className="block w-full px-3 py-3 text-sm border border-gray-300 rounded-lg shadow-sm focus:ring-navy focus:border-navy" placeholder="123 Main St" data-id="element-1429" data-mp="mgy8bcckvdi" />
              </div>
              <div data-id="element-1430" data-mp="oc1u1utlnn47">
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1431" data-mp="jj8nr2ihg6q0">
                  Zip Code
                </label>
                <input type="text" id="zipCode" value={formData.zipCode} onChange={e => updateFormData('zipCode', e.target.value)} className={inputClasses('zipCode')} placeholder="12345" maxLength={5} data-id="element-1432" data-mp="ejdzl6q3dv8n" />
                {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-1433" data-mp="177i2xll6xw86">{errors.zipCode}</p>}
              </div>
            </div>

            {/* TCPA */}
            <TCPAConsent checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} data-id="element-1434" data-mp="36g9p31yyyk8v" />

            {/* Submit */}
            <button type="submit" className="w-full inline-flex items-center justify-center h-[52px] px-8 bg-primary hover:bg-primary-hover active:bg-secondary text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" data-id="element-1435" data-mp="2rp12l1qd5ezy">
              Get My Results
            </button>

            {/* Security note */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400 pt-2" data-id="element-1436" data-mp="2drm99lwzon2p">
              <LockIcon className="w-3.5 h-3.5" data-id="element-1437" data-mp="7161nj2ikl1u" />
              Your information is secure and will never be sold.
            </div>
          </form>
        </div>
      </motion.div>
    </main>;
}